import { memo, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  CartesianGrid,
  ResponsiveContainer,
  ScatterChart as RechartScatterChart,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
  Scatter,
  TooltipProps,
} from 'recharts';
import { ScatterPointItem } from 'recharts/types/cartesian/Scatter';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { theme } from '@frontend/theme';
import { EmptyData } from '../atoms/empty-data';
import { renderHorizontalMarker } from '../atoms/horizontal-marker';
import { Tooltip } from '../atoms/tooltip';
import { useChartContext } from '../chart.provider';
import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { defaultFormatter } from '../helpers/chart-utils';
import { showEmptyStateForScatterChart } from '../helpers/show-empty-state-calc';
import { Appearance, ScatterChartData, Value } from './types';

type Props = {
  appearance?: Appearance;
  data?: ScatterChartData;
  tooltipRenderer?: (props: TooltipProps<ValueType, NameType>) => React.ReactNode;
};

const defaultMargin = { top: 20, right: 40, bottom: 8, left: 0 };

const axisLine = {
  stroke: theme.colors.neutral10,
  strokeDasharray: '1 1',
  strokeWidth: 1,
};

export const ScatterChart = memo(({ appearance = {}, data = { groups: [] }, tooltipRenderer }: Props) => {
  const {
    customTooltipTitle,
    customXAxisTick,
    customXAxisTickFormat,
    customYAxisTick,
    customYAxisTickFormat,
    strokeWidth = 2,
    gridBackgroundColor,
    height = 240,
    hideHoveringCursor,
    hideMarkerEnds,
    hideTooltip,
    margin,
    showGridLines,
    showXAxis,
    showYAxis,
    tooltipLabelsTransform,
    xAxis,
    yAxis,
    width = '100%',
    wrapperStyles,
  } = appearance;
  const updatedMargin = { ...defaultMargin, ...margin };

  const { activeLegends, colors, emptyStateConfig, labels, setPrimaryLegendsList, strokeColors } = useChartContext();

  const uniqueXAxisDataSeries = useMemo(() => {
    const groups = data?.groups ?? [];
    const series = groups.flatMap((group) => (group.values ?? []).map((value) => value.x));
    return Array.from(new Set(series));
  }, [data?.groups]);

  const chartData: Record<string, Value[]> = useMemo(() => {
    if (!data?.groups?.length) {
      return {};
    }

    return data.groups.reduce(
      (acc, { name, values }) => ({ ...acc, [name]: values.map((dataKeys) => ({ ...dataKeys, name })) }),
      {}
    );
  }, [data.groups]);

  const showEmptyState = useMemo(() => {
    return showEmptyStateForScatterChart({
      ...emptyStateConfig,
      data,
    });
  }, [data, emptyStateConfig]);

  const { domain, xAxisTicks } = useMemo(() => {
    if (!uniqueXAxisDataSeries.length) {
      return {
        domain: undefined,
        xAxisTicks: undefined,
      };
    }

    let min = Infinity;
    let max = -Infinity;

    for (const x of uniqueXAxisDataSeries) {
      if (x < min) min = x;
      if (x > max) max = x;
    }

    if (min === Infinity || max === -Infinity) {
      return {
        domain: undefined,
        xAxisTicks: undefined,
      };
    }

    const padding = (max - min) * 0.05;
    const domain = [min - padding, max + padding];

    const xAxisTicks = [];
    const rangeMin = min;
    const rangeMax = max;
    const interval = (rangeMax - rangeMin) / 4;

    for (let i = 0; i <= 4; i++) {
      xAxisTicks.push(Math.round(rangeMin + i * interval));
    }

    return { domain, xAxisTicks };
  }, [uniqueXAxisDataSeries]);

  useEffect(() => {
    setPrimaryLegendsList(data.groups.map(({ name }) => name) ?? []);
  }, [data.groups]);

  return (
    <div css={[styles.mainWrapper, wrapperStyles]}>
      <div
        className='pdf-chart-view'
        style={{
          height,
          minHeight: showEmptyState ? EMPTY_STATE_MIN_HEIGHT : height,
          width,
        }}
      >
        {!showEmptyState ? (
          <ResponsiveContainer width='100%' height='100%' key={JSON.stringify(data.groups || {})}>
            <RechartScatterChart margin={updatedMargin}>
              {showGridLines && (
                <CartesianGrid stroke={theme.colors.neutral10} fill={gridBackgroundColor} strokeDasharray='1 1' />
              )}

              {renderHorizontalMarker(data.markers, colors, hideMarkerEnds)}
              <XAxis
                axisLine={axisLine}
                dataKey='x'
                domain={xAxis?.domain ?? domain}
                dy={8}
                fontSize={14}
                hide={!showXAxis}
                interval='equidistantPreserveStart'
                tickCount={xAxis?.tickCount}
                tick={
                  customXAxisTick
                    ? (props) => {
                        return customXAxisTick?.({
                          groupName: props.payload.value,
                          labels,
                          x: props.x,
                          y: props.y,
                        });
                      }
                    : undefined
                }
                tickFormatter={customXAxisTickFormat}
                tickLine={xAxis?.tickLine ?? false}
                padding={xAxis?.padding}
                ticks={xAxis?.tickCount ? undefined : xAxisTicks}
                type='number'
              />

              <YAxis
                axisLine={axisLine}
                tickCount={yAxis?.tickCount}
                scale={yAxis?.scale}
                domain={yAxis?.domain}
                dataKey='y'
                fontSize={14}
                hide={!showYAxis}
                tickFormatter={customYAxisTickFormat}
                tick={
                  customYAxisTick
                    ? (props) => {
                        return customYAxisTick?.({
                          groupName: props.payload.value,
                          labels,
                          x: props.x,
                          y: props.y,
                        });
                      }
                    : undefined
                }
                tickLine={false}
                type='number'
                width={yAxis?.width}
              />

              {activeLegends.map((legend) => {
                const color = colors[legend] || theme.colors.neutral50;
                return (
                  <Scatter
                    data={chartData?.[legend]}
                    key={legend}
                    name={legend}
                    shape={(props: ScatterPointItem) => {
                      const pointData = props?.payload ?? { y: 0 };
                      return (
                        <circle
                          {...props}
                          fill={color}
                          r={pointData.size}
                          stroke={strokeColors?.[legend]}
                          strokeWidth={strokeWidth}
                        />
                      );
                    }}
                  />
                );
              })}

              <RechartTooltip
                content={(props) => {
                  const { active, payload } = props;

                  if (!payload?.length || !active || hideTooltip) {
                    return null;
                  }

                  const { x = 0, y = 0, size = 0, name } = payload?.[0]?.payload || {};

                  return (
                    <div css={styles.container}>
                      {tooltipRenderer?.(props) ?? (
                        <Tooltip
                          noContainer
                          data={[
                            {
                              color: colors[name],
                              formattedValue: defaultFormatter(size),
                              groupId: name,
                              id: 'size',
                              label: labels[y] || labels[name],
                              value: size,
                            },
                          ]}
                          labelsTransform={tooltipLabelsTransform}
                          name={
                            customTooltipTitle?.({
                              groupName: name,
                            }) || customXAxisTickFormat?.(x, 0)
                          }
                          skipActiveLegendsCheck
                        />
                      )}
                    </div>
                  );
                }}
                cursor={
                  hideHoveringCursor
                    ? false
                    : {
                        fill: theme.colors.neutral20,
                        strokeDasharray: '10 3',
                        strokeWidth: 1,
                      }
                }
              />
            </RechartScatterChart>
          </ResponsiveContainer>
        ) : (
          <EmptyData emptyStateConfig={emptyStateConfig} />
        )}
      </div>
    </div>
  );
});

ScatterChart.displayName = 'ScatterChart';

const styles = {
  mainWrapper: css`
    overflow: auto;
    position: relative;
  `,

  container: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    overflow: hidden;
    pointer-events: none;
    z-index: ${theme.zIndex.modals + 1}; // set to place above modals
  `,
};
